import { MyError } from '@/constants/error';
import { Paths } from '@/constants/paths';
import { useSnackbar } from '@/hooks/use-snackbar';
import { loginAtom, logoutAtom } from '@/stores/auth';
import { programAtom } from '@/stores/program';
import { useLocation, useNavigate } from '@remix-run/react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import base58 from 'bs58';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import useSWRSubscription from 'swr/subscription';
import tweetnacl from 'tweetnacl-util';

export const useDisconnect = () => {
  const { disconnect } = useWallet();
  const logout = useSetAtom(logoutAtom);

  return async () => {
    logout();
    await disconnect();
  };
};

export const useBalance = () => {
  const { connection } = useConnection();
  const { publicKey } = useWallet();

  return useSWRSubscription(
    publicKey ? [connection.getBalance.name, publicKey.toBase58()] : null,
    ([, b58], { next }) => {
      const pubkey = new PublicKey(b58);
      const subscribeId = connection.onAccountChange(pubkey, (data) => {
        next(null, data.lamports);
      });

      connection.getBalance(pubkey).then((b) => {
        next(null, b);
      });

      return () => {
        connection.removeAccountChangeListener(subscribeId);
      };
    }
  );
};

export const useLogout = () => {
  const { disconnect } = useWallet();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return useAtomCallback(
    useCallback(
      async (
        get,
        set,
        opts: { disconnect: boolean } = { disconnect: true }
      ) => {
        if (opts?.disconnect) {
          await disconnect();
        }
        if (pathname.startsWith(Paths.Admin)) {
          navigate(Paths.Home, { replace: true });
        }
        set(logoutAtom);
      },
      [disconnect, navigate, pathname]
    )
  );
};

export const useAdminLogin = () => {
  const { signMessage } = useWallet();
  const program = useAtomValue(programAtom);
  const login = useSetAtom(loginAtom);
  const { enqueueSnackbar } = useSnackbar();
  const signAndLogin = useCallback(
    async function sign() {
      try {
        if (!program) return;
        const now = Date.now();
        // eslint-disable-next-line lingui/no-unlocalized-strings
        const msg = `You are connecting to the admin wallet.\nTo proceed with admin operations, please confirm this request.\n\nNonce: ${now}`;
        const bufferMsg = tweetnacl.decodeUTF8(msg);
        const signed = await signMessage?.(bufferMsg);
        if (!signed) return;
        const signature = base58.encode(signed);
        await login({ signature, timestamp: now });

        return true;
      } catch (err: any) {
        if (err.__msg) {
          enqueueSnackbar(err.__msg, { variant: 'error' });
        }

        if (err.json?.message === MyError.EXPIRED_NONCE) {
          return sign();
        }
        return false;
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [program, signMessage]
  );

  if (!program || !signMessage) return null;

  return signAndLogin;
};
